// @ts-check
// Registro de comandos contextuais (doc 15, seções 3, 6 e 11; contrato 08-contrato-comandos-atalhos): decide quais
// ações aparecem conforme onde a pessoa está (lista, seleção, registro) e o que o papel dela permite. É puro: recebe
// o contexto já montado e devolve descritores com chave de tradução; quem executa é
// client/src/modules/command-menu/command-actions.js. O menu de comandos (Cmd+K), o clique direito na linha ou no card,
// a barra de seleção e os botões fixados do cabeçalho leem daqui, então as quatro superfícies mostram as mesmas ações.
import { PINNABLE_LIST_COMMAND_KEYS } from '../../../shared/command-menu/pinned-commands.js';

/**
 * @typedef {'selection' | 'record' | 'conversation' | 'list' | 'settings'} CommandGroup
 * @typedef {{ canReadObjectRecords?: boolean, canUpdateObjectRecords?: boolean, canSoftDeleteObjectRecords?: boolean, canDestroyObjectRecords?: boolean }} CommandObjectPermissions
 * @typedef {{ nameSingular: string, namePlural: string, labelSingular: string, labelPlural: string, isSystem?: boolean,
 *   duplicateCriteria?: string[][] | null, permissions?: CommandObjectPermissions }} CommandObject
 * @typedef {{ count: number, isTrashMode?: boolean, hasOwnerField?: boolean, hasStageField?: boolean, stageFieldLabel?: string,
 *   supportsNotLead?: boolean, isFlowDrilldown?: boolean }} CommandSelection
 * isFlowDrilldown: a lista foi aberta por um contador de fluxo (chip de fluxo), onde "Remover do fluxo" faz sentido
 * @typedef {{ id: string, isDeleted?: boolean, isFavorite?: boolean, canClassifyNotLead?: boolean,
 *   marketingOptOut?: boolean, contactMarketingOptOut?: boolean, pointOfContactId?: string | null, canMarkMarketingOptOut?: boolean }} CommandRecord
 * @typedef {{ threadId: string, runningBotName?: string | null, startableBots?: ReadonlyArray<{ id: string, name: string }>, isServiceWindowOpen?: boolean | null }} CommandConversation
 * conversa aberta (Conversas ou Caixa de atendimento, 3.0.0): o bot que conduz e os bots manuais que o número oferece
 * marketingOptOut (pessoa) e contactMarketingOptOut (card, espelho do contato principal): LGPD não consentida (2.0.0)
 * @typedef {{
 *   page: 'recordIndex' | 'recordShow' | 'other',
 *   object?: CommandObject | null,
 *   view?: { id: string, type: string, key?: string | null } | null,
 *   viewTypes?: string[],
 *   isTrashMode?: boolean,
 *   selection?: CommandSelection | null,
 *   record?: CommandRecord | null,
 *   conversation?: CommandConversation | null,
 *   hasPermissionFlag: (flag: string) => boolean,
 *   isCoordinationFocused?: boolean,
 *   canCreateTask?: boolean,
 *   canCreateNote?: boolean,
 *   campaignCommandIds?: ReadonlySet<string>,
 *   emailCampaignObjectNames?: ReadonlySet<string>,
 *   isModuleEnabled?: (moduleKey: string) => boolean,
 * }} CommandContext
 * campaignCommandIds: comandos da 2.0.0 que a área dona já entregou (client/src/modules/campaigns/campaign-commands.js);
 * os outros não aparecem; desde a 3.0.0, também os do atendimento e da retenção (SERVICE_DESK_COMMAND_IDS e
 * RETENTION_COMMAND_IDS), que pedem ainda o módulo ligado (isModuleEnabled). emailCampaignObjectNames: objetos que podem ser
 * público de campanha de e-mail.
 * @typedef {{ id: string, group: CommandGroup, labelKey: string, labelParams?: Record<string, unknown>, icon: string,
 *   accent?: 'danger', hotKeys?: string[], href?: string, pinnableKey?: string, isBarButton?: boolean }} CommandDescriptor
 */

// favoritar cria um item por registro; acima disso o menu deixaria de ser um atalho
export const MAXIMUM_FAVORITES_AT_ONCE = 20;
// a mescla do motor aceita de 2 a 9 registros (data/parity.js)
export const MAXIMUM_MERGE_RECORDS = 9;
export const VIEW_LAYOUT_TYPES = Object.freeze(['TABLE', 'KANBAN', 'CALENDAR']);
// membros da equipe vêm de convites; atendimentos e departamentos nascem só pelo atendimento (3.0.0); o motor também recusa
const NOT_IMPORTABLE_OBJECTS = new Set(['workspaceMember', 'serviceTicket', 'serviceTicketStudent', 'serviceTeam']);
// módulo Atendimento (3.0.0, contrato 10 de atendimento, seção 25)
const SERVICE_DESK_MODULE = 'SERVICE_DESK';
const SERVICE_TICKET_OBJECT_NAME = 'serviceTicket';
// a mescla de atendimentos aceita de 2 a 10
export const MAXIMUM_TICKET_MERGE_RECORDS = 10;
// objetos com a aba Conversas (contrato de conversas, seção 6)
const CONVERSATION_OBJECT_NAMES = new Set(['person', 'enrollmentOpportunity']);
// objetos que podem ser público de campanha de e-mail (contrato 08 de e-mail avançado); a área de e-mail troca pelo
// contexto (emailCampaignObjectNames) quando o público passa a ser só o card (2.0.0)
const CAMPAIGN_OBJECT_NAMES = new Set(['person', 'enrollmentOpportunity']);
// público das campanhas de WhatsApp e dos fluxos da 2.0.0: o card do funil
const CARD_OBJECT_NAME = 'enrollmentOpportunity';
// tarefas e notas não ganham tarefa ou nota vinculada a si mesmas
const ACTIVITY_OBJECT_NAMES = new Set(['task', 'note']);
const VIEW_LAYOUT_ICONS = /** @type {Record<string, string>} */ ({ TABLE: 'IconTable', KANBAN: 'IconLayoutKanban', CALENDAR: 'IconCalendar' });

/**
 * Páginas de Configurações e o que o papel precisa para vê-las, na ordem da gaveta (doc 15, seção 11.7).
 * `anyFlag`: basta uma das flags; `hiddenForCoordination`: papéis focados na coordenação não veem.
 * @type {ReadonlyArray<{ key: string, labelKey: string, href: string, icon: string, anyFlag?: string[], hiddenForCoordination?: boolean }>}
 */
export const SETTINGS_PAGES = Object.freeze([
  { key: 'profile', labelKey: 'settings.profile.title', href: '/settings/profile', icon: 'IconUserCircle' },
  { key: 'notifications', labelKey: 'notifications.settingsTitle', href: '/settings/notifications', icon: 'IconBell' },
  { key: 'accounts', labelKey: 'calendar.accounts.title', href: '/settings/accounts', icon: 'IconAt' },
  { key: 'objects', labelKey: 'settings.objects.title', href: '/settings/objects', icon: 'IconHierarchy2', hiddenForCoordination: true },
  { key: 'funnel', labelKey: 'dataModel.funnel.title', href: '/settings/funnel', icon: 'IconLayoutKanban', hiddenForCoordination: true },
  { key: 'channels', labelKey: 'settings.channels.title', href: '/settings/channels', icon: 'IconPlugConnected', hiddenForCoordination: true },
  { key: 'whatsappTemplates', labelKey: 'whatsappTemplates.title', href: '/settings/whatsapp-templates', icon: 'IconClipboardText', hiddenForCoordination: true },
  { key: 'members', labelKey: 'team.members.title', href: '/settings/members', icon: 'IconUsers', anyFlag: ['WORKSPACE_MEMBERS', 'ROLES'] },
  { key: 'roles', labelKey: 'team.roles.title', href: '/settings/roles', icon: 'IconShieldLock', anyFlag: ['ROLES'] },
  { key: 'automations', labelKey: 'automations.title', href: '/settings/automations', icon: 'IconSettingsAutomation', anyFlag: ['WORKFLOWS'] },
  { key: 'captureForms', labelKey: 'leadCapture.forms.title', href: '/settings/capture-forms', icon: 'IconFileDescription', anyFlag: ['WORKSPACE'] },
  { key: 'bookingPages', labelKey: 'bookingPages.title', href: '/settings/booking-pages', icon: 'IconCalendarPlus', anyFlag: ['WORKSPACE'] },
  { key: 'developers', labelKey: 'leadCapture.developers.title', href: '/settings/developers', icon: 'IconApi', anyFlag: ['API_KEYS_AND_WEBHOOKS'] },
  { key: 'billing', labelKey: 'operations.billing.title', href: '/settings/billing', icon: 'IconReceipt', anyFlag: ['BILLING'] },
  { key: 'privacy', labelKey: 'operations.privacy.title', href: '/settings/privacy', icon: 'IconShieldLock', anyFlag: ['SECURITY'] },
  { key: 'auditLog', labelKey: 'operations.audit.title', href: '/settings/audit-log', icon: 'IconHistory', anyFlag: ['SECURITY'] },
]);

/** @param {CommandObject | null | undefined} object @param {keyof CommandObjectPermissions} permission */
const can = (object, permission) => Boolean(object) && object?.permissions?.[permission] !== false;

/** Comando da 2.0.0 já entregue pela área dona (spec-2.0.0 seção 5.3). @param {CommandContext} context @param {string} commandId */
const isCampaignCommandReady = (context, commandId) => Boolean(context.campaignCommandIds?.has(commandId));

/**
 * Comando do módulo Atendimento (3.0.0): entregue pela área dona, módulo ligado na escola e a flag SERVICE_DESK.
 * @param {CommandContext} context @param {string} commandId
 */
const isServiceDeskCommandReady = (context, commandId) => isCampaignCommandReady(context, commandId)
  && Boolean(context.isModuleEnabled?.(SERVICE_DESK_MODULE)) && context.hasPermissionFlag('SERVICE_DESK');

/** @param {CommandContext} context @param {CommandObject} object */
const acceptsEmailCampaign = (context, object) => (context.emailCampaignObjectNames ?? CAMPAIGN_OBJECT_NAMES).has(object.nameSingular);

/**
 * Ações sobre os registros selecionados (as mesmas da barra de seleção). Na lixeira só Restaurar e Excluir
 * definitivamente. Com um registro só, o clique direito também oferece Abrir.
 * @param {CommandContext} context
 * @returns {CommandDescriptor[]}
 */
export function buildSelectionCommands(context) {
  const { object, selection } = context;
  if (!object || !selection || selection.count <= 0) return [];
  const objectParams = { object: object.labelPlural, count: selection.count };
  /** @type {CommandDescriptor[]} */
  const commands = [];
  const push = (/** @type {Omit<CommandDescriptor, 'group'>} */ command) => commands.push({ group: 'selection', labelParams: objectParams, ...command });
  if (selection.count === 1) push({ id: 'selection.open', labelKey: 'commands.selection.open', icon: 'IconExternalLink' });
  if (selection.isTrashMode) {
    if (can(object, 'canSoftDeleteObjectRecords')) push({ id: 'selection.restore', labelKey: 'commands.selection.restore', icon: 'IconRestore' });
    if (can(object, 'canDestroyObjectRecords')) push({ id: 'selection.destroy', labelKey: 'commands.selection.destroy', icon: 'IconTrashX', accent: 'danger' });
    return commands;
  }
  const canUpdate = can(object, 'canUpdateObjectRecords');
  const canDelete = can(object, 'canSoftDeleteObjectRecords');
  if (canUpdate) push({ id: 'selection.update', labelKey: 'commands.selection.update', icon: 'IconEdit', isBarButton: true });
  if (canUpdate && selection.hasOwnerField) push({ id: 'selection.assign', labelKey: 'commands.selection.assign', icon: 'IconUserCircle', isBarButton: true });
  if (canUpdate && selection.hasStageField) {
    push({ id: 'selection.changeStage', labelKey: 'commands.selection.changeStage', labelParams: { ...objectParams, field: selection.stageFieldLabel ?? '' }, icon: 'IconProgressCheck', isBarButton: true });
  }
  if (selection.supportsNotLead && canUpdate) push({ id: 'selection.notLead', labelKey: 'commands.selection.notLead', icon: 'IconUserOff', isBarButton: true });
  if (canUpdate && canDelete && Boolean(object.duplicateCriteria?.length) && selection.count >= 2 && selection.count <= MAXIMUM_MERGE_RECORDS) {
    push({ id: 'selection.merge', labelKey: 'commands.selection.merge', icon: 'IconArrowMerge' });
  }
  if (context.hasPermissionFlag('EXPORT_CSV')) push({ id: 'selection.export', labelKey: 'commands.selection.export', icon: 'IconFileExport' });
  if (context.hasPermissionFlag('EMAIL_CAMPAIGNS') && acceptsEmailCampaign(context, object)) {
    push({ id: 'selection.emailCampaign', labelKey: 'commands.selection.emailCampaign', icon: 'IconSend' });
  }
  // 2.0.0: campanha de WhatsApp e fluxos, só sobre o card
  if (object.nameSingular === CARD_OBJECT_NAME) {
    if (context.hasPermissionFlag('WHATSAPP_CAMPAIGNS') && isCampaignCommandReady(context, 'selection.whatsappCampaign')) {
      push({ id: 'selection.whatsappCampaign', labelKey: 'commands.selection.whatsappCampaign', icon: 'IconBrandWhatsapp' });
    }
    if (context.hasPermissionFlag('WORKFLOWS') && isCampaignCommandReady(context, 'selection.addToFlow')) {
      push({ id: 'selection.addToFlow', labelKey: 'commands.selection.addToFlow', icon: 'IconSitemap' });
    }
    if (selection.isFlowDrilldown && context.hasPermissionFlag('WORKFLOWS') && isCampaignCommandReady(context, 'selection.removeFromFlow')) {
      push({ id: 'selection.removeFromFlow', labelKey: 'commands.selection.removeFromFlow', icon: 'IconSitemap', accent: 'danger' });
    }
  }
  if (object.nameSingular === SERVICE_TICKET_OBJECT_NAME && selection.count >= 2 && selection.count <= MAXIMUM_TICKET_MERGE_RECORDS
    && isServiceDeskCommandReady(context, 'selection.mergeTickets')) {
    push({ id: 'selection.mergeTickets', labelKey: 'commands.selection.mergeTickets', icon: 'IconArrowMerge' });
  }
  if (selection.count <= MAXIMUM_FAVORITES_AT_ONCE) push({ id: 'selection.favorite', labelKey: 'commands.selection.favorite', icon: 'IconHeart' });
  if (canDelete) push({ id: 'selection.delete', labelKey: 'commands.selection.delete', icon: 'IconTrash', accent: 'danger' });
  return commands;
}

/**
 * Ações do registro aberto (doc 15, seção 11.1): favoritar, exportar, mesclar com duplicata, tarefa e nota
 * vinculadas, conversa, Não é lead e excluir; registro na lixeira só Restaurar e Excluir definitivamente.
 * @param {CommandContext} context
 * @returns {CommandDescriptor[]}
 */
export function buildRecordCommands(context) {
  const { object, record } = context;
  if (context.page !== 'recordShow' || !object || !record) return [];
  const params = { object: object.labelSingular };
  /** @type {CommandDescriptor[]} */
  const commands = [];
  const push = (/** @type {Omit<CommandDescriptor, 'group'>} */ command) => commands.push({ group: 'record', labelParams: params, ...command });
  if (record.isDeleted) {
    if (can(object, 'canSoftDeleteObjectRecords')) push({ id: 'record.restore', labelKey: 'commands.record.restore', icon: 'IconRestore' });
    if (can(object, 'canDestroyObjectRecords')) push({ id: 'record.destroy', labelKey: 'commands.record.destroy', icon: 'IconTrashX', accent: 'danger' });
    return commands;
  }
  push(record.isFavorite
    ? { id: 'record.unfavorite', labelKey: 'commands.record.unfavorite', icon: 'IconHeartOff' }
    : { id: 'record.favorite', labelKey: 'commands.record.favorite', icon: 'IconHeart' });
  if (!ACTIVITY_OBJECT_NAMES.has(object.nameSingular)) {
    if (context.canCreateTask) push({ id: 'record.createTask', labelKey: 'commands.record.createTask', icon: 'IconCheckbox' });
    if (context.canCreateNote) push({ id: 'record.createNote', labelKey: 'commands.record.createNote', icon: 'IconNotes' });
  }
  if (CONVERSATION_OBJECT_NAMES.has(object.nameSingular) && context.hasPermissionFlag('CONVERSATIONS')) {
    push({ id: 'record.newConversation', labelKey: 'commands.record.newConversation', icon: 'IconMessages' });
  }
  const canUpdate = can(object, 'canUpdateObjectRecords');
  const canDelete = can(object, 'canSoftDeleteObjectRecords');
  if (canUpdate && canDelete && Boolean(object.duplicateCriteria?.length)) {
    push({ id: 'record.merge', labelKey: 'commands.record.merge', icon: 'IconArrowMerge' });
  }
  if (context.hasPermissionFlag('EXPORT_CSV')) push({ id: 'record.export', labelKey: 'commands.record.export', icon: 'IconFileExport' });
  if (record.canClassifyNotLead) push({ id: 'record.notLead', labelKey: 'commands.record.notLead', icon: 'IconUserOff' });
  // 2.0.0: LGPD não consentida da pessoa ou do contato principal do card, registrar consentimento e inserir num fluxo
  if (isCampaignCommandReady(context, 'record.marketingOptOut') && record.canMarkMarketingOptOut) {
    if (object.nameSingular === 'person' && record.marketingOptOut !== true) {
      push({ id: 'record.marketingOptOut', labelKey: 'commands.record.marketingOptOut', icon: 'IconForbid', accent: 'danger' });
    } else if (object.nameSingular === CARD_OBJECT_NAME && record.contactMarketingOptOut !== true && record.pointOfContactId) {
      push({ id: 'record.marketingOptOut', labelKey: 'commands.record.contactMarketingOptOut', icon: 'IconForbid', accent: 'danger' });
    }
  }
  if (isCampaignCommandReady(context, 'record.marketingConsent') && object.nameSingular === 'person' && record.marketingOptOut === true && context.hasPermissionFlag('SECURITY')) {
    push({ id: 'record.marketingConsent', labelKey: 'commands.record.marketingConsent', icon: 'IconShieldCheck' });
  }
  if (isCampaignCommandReady(context, 'record.addToFlow') && object.nameSingular === CARD_OBJECT_NAME && context.hasPermissionFlag('WORKFLOWS')) {
    push({ id: 'record.addToFlow', labelKey: 'commands.record.addToFlow', icon: 'IconSitemap' });
  }
  // 3.0.0: atendimento pela pessoa ou família, ações do atendimento aberto e retenção da família (áreas F3 e F5)
  if ((object.nameSingular === 'person' || object.nameSingular === 'household') && isServiceDeskCommandReady(context, 'record.openServiceTicket')) {
    push({ id: 'record.openServiceTicket', labelKey: 'commands.record.openServiceTicket', icon: 'IconHeadset' });
  }
  if (object.nameSingular === SERVICE_TICKET_OBJECT_NAME) {
    for (const [id, icon] of /** @type {Array<[string, string]>} */ ([
      ['record.takeTicket', 'IconUserCheck'], ['record.transferTicket', 'IconTransferIn'], ['record.resolveTicket', 'IconCircleCheck'], ['record.openInInbox', 'IconInbox'],
    ])) {
      if (isServiceDeskCommandReady(context, id)) push({ id, labelKey: `commands.${id}`, icon });
    }
  }
  if (object.nameSingular === 'household') {
    for (const [id, icon] of /** @type {Array<[string, string]>} */ ([['record.markRetentionRisk', 'IconAlertTriangle'], ['record.openRetentionCase', 'IconHeartHandshake']])) {
      if (isServiceDeskCommandReady(context, id)) push({ id, labelKey: `commands.${id}`, icon });
    }
  }
  if (canDelete) push({ id: 'record.delete', labelKey: 'commands.record.delete', icon: 'IconTrash', accent: 'danger' });
  return commands;
}

/**
 * Ações da conversa aberta (3.0.0, contrato de bots, seção 22.4): "Iniciar o bot <nome>" para cada bot manual do número
 * (com a janela de 24 h aberta e nenhum bot conduzindo) e "Assumir conversa" quando um bot conduz. As mesmas da faixa
 * do bot na conversa; pedem o módulo ligado e CONVERSATIONS ou SERVICE_DESK, como as rotas.
 * @param {CommandContext} context
 * @returns {CommandDescriptor[]}
 */
export function buildConversationCommands(context) {
  const { conversation } = context;
  if (!conversation?.threadId || !context.isModuleEnabled?.(SERVICE_DESK_MODULE)) return [];
  if (!context.hasPermissionFlag('CONVERSATIONS') && !context.hasPermissionFlag('SERVICE_DESK')) return [];
  /** @type {CommandDescriptor[]} */
  const commands = [];
  if (conversation.runningBotName) {
    if (isCampaignCommandReady(context, 'conversation.takeOverBot')) {
      commands.push({ id: 'conversation.takeOverBot', group: 'conversation', labelKey: 'commands.conversation.takeOverBot', labelParams: { name: conversation.runningBotName }, icon: 'IconHandStop' });
    }
    return commands;
  }
  if (conversation.isServiceWindowOpen === false || !isCampaignCommandReady(context, 'conversation.startBot')) return commands;
  for (const bot of conversation.startableBots ?? []) {
    commands.push({ id: `conversation.startBot:${bot.id}`, group: 'conversation', labelKey: 'commands.conversation.startBot', labelParams: { name: bot.name, botId: bot.id }, icon: 'IconRobot' });
  }
  return commands;
}

/**
 * Ações da lista aberta (doc 15, seção 8: Importar, Exportar visualização, Ver excluídos, Criar visualização), mais
 * mudar o tipo de view, filtrar e ordenar. As fixáveis levam `pinnableKey`.
 * @param {CommandContext} context
 * @returns {CommandDescriptor[]}
 */
export function buildListCommands(context) {
  const { object, view } = context;
  if (context.page !== 'recordIndex' || !object || !view) return [];
  const params = { object: object.labelPlural };
  /** @type {CommandDescriptor[]} */
  const commands = [];
  const push = (/** @type {Omit<CommandDescriptor, 'group'>} */ command) => commands.push({ group: 'list', labelParams: params, ...command });
  if (context.hasPermissionFlag('IMPORT_CSV') && can(object, 'canUpdateObjectRecords') && !object.isSystem && !NOT_IMPORTABLE_OBJECTS.has(object.nameSingular)) {
    push({ id: 'list.import', labelKey: 'commands.list.import', icon: 'IconUpload', pinnableKey: 'import' });
  }
  if (context.hasPermissionFlag('EXPORT_CSV')) push({ id: 'list.export', labelKey: 'commands.list.export', icon: 'IconDownload', pinnableKey: 'export' });
  if (!context.isTrashMode && context.hasPermissionFlag('EMAIL_CAMPAIGNS') && acceptsEmailCampaign(context, object)) {
    push({ id: 'list.emailCampaign', labelKey: 'commands.list.emailCampaign', icon: 'IconSend' });
  }
  if (!context.isTrashMode && object.nameSingular === SERVICE_TICKET_OBJECT_NAME && isServiceDeskCommandReady(context, 'list.newServiceTicket')) {
    push({ id: 'list.newServiceTicket', labelKey: 'commands.list.newServiceTicket', icon: 'IconPlus' });
  }
  if (!context.isTrashMode && object.nameSingular === CARD_OBJECT_NAME && context.hasPermissionFlag('WHATSAPP_CAMPAIGNS') && isCampaignCommandReady(context, 'list.whatsappCampaign')) {
    push({ id: 'list.whatsappCampaign', labelKey: 'commands.list.whatsappCampaign', icon: 'IconBrandWhatsapp' });
  }
  push(context.isTrashMode
    ? { id: 'list.hideDeleted', labelKey: 'commands.list.hideDeleted', icon: 'IconEyeOff', pinnableKey: 'seeDeleted' }
    : { id: 'list.seeDeleted', labelKey: 'commands.list.seeDeleted', icon: 'IconTrash', pinnableKey: 'seeDeleted' });
  push({ id: 'list.createView', labelKey: 'commands.list.createView', icon: 'IconPlus', pinnableKey: 'createView' });
  for (const type of VIEW_LAYOUT_TYPES) {
    if (type === view.type) continue;
    // sem view desse tipo, o comando abre a criação já com o tipo escolhido
    const exists = (context.viewTypes ?? []).includes(type);
    push({ id: `list.layout.${type}`, labelKey: exists ? `commands.list.layout.${type}` : `commands.list.createLayout.${type}`, icon: VIEW_LAYOUT_ICONS[type] });
  }
  if (!context.isTrashMode) {
    push({ id: 'list.filter', labelKey: 'commands.list.filter', icon: 'IconFilter', pinnableKey: 'filter' });
    push({ id: 'list.sort', labelKey: 'commands.list.sort', icon: 'IconSortDescending', pinnableKey: 'sort' });
  }
  return commands;
}

/**
 * "Ir para Configurações" e uma entrada por página que o papel pode ver (doc 15, seção 11.7).
 * @param {CommandContext} context
 * @returns {CommandDescriptor[]}
 */
export function buildSettingsCommands(context) {
  return SETTINGS_PAGES
    .filter((page) => !(page.hiddenForCoordination && context.isCoordinationFocused))
    .filter((page) => !page.anyFlag || page.anyFlag.some((flag) => context.hasPermissionFlag(flag)))
    .map((page) => (page.key === 'profile'
      ? { id: 'settings.profile', group: /** @type {CommandGroup} */ ('settings'), labelKey: 'commands.settings.goToSettings', icon: 'IconSettings', href: page.href, hotKeys: ['G', 'S'] }
      : { id: `settings.${page.key}`, group: /** @type {CommandGroup} */ ('settings'), labelKey: 'commands.settings.goToPage', labelParams: { pageKey: page.labelKey }, icon: page.icon, href: page.href }));
}

/**
 * Todas as ações do contexto, na ordem dos grupos: seleção, registro, conversa, lista e configurações.
 * @param {CommandContext} context
 * @returns {CommandDescriptor[]}
 */
export function buildContextualCommands(context) {
  return [
    ...buildSelectionCommands(context),
    ...buildRecordCommands(context),
    ...buildConversationCommands(context),
    ...(context.selection && context.selection.count > 0 ? [] : buildListCommands(context)),
    ...buildSettingsCommands(context),
  ];
}

/**
 * Botões fixados que o papel pode usar, na ordem gravada na view; os demais (sem permissão ou fora do contexto) somem.
 * @param {CommandDescriptor[]} listCommands @param {string[] | undefined} pinnedKeys
 */
export function resolvePinnedListCommands(listCommands, pinnedKeys) {
  return (pinnedKeys ?? [])
    .filter((key) => PINNABLE_LIST_COMMAND_KEYS.includes(key))
    .map((key) => listCommands.find((command) => command.pinnableKey === key))
    .filter((command) => command !== undefined);
}

/**
 * Remove acentos e caixa (mesma regra da busca do menu, doc 15, seção 8).
 * @param {string} text
 */
export function normalizeCommandText(text) {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/**
 * Busca por "contém" no rótulo ou nas teclas do atalho (digitar "gs" acha "Ir para Configurações").
 * @template {{ text: string, hotKeys?: string[] }} TItem
 * @param {TItem[]} items @param {string} searchText
 * @returns {TItem[]}
 */
export function filterCommandItems(items, searchText) {
  const search = normalizeCommandText(searchText);
  if (!search) return items;
  return items.filter((item) => normalizeCommandText(item.text).includes(search) || normalizeCommandText((item.hotKeys ?? []).join('')).includes(search));
}

/**
 * Seleção depois do clique direito (doc 15, seção 6.2): a linha clicada fora da seleção vira a seleção sozinha; dentro
 * dela, a seleção inteira fica e as ações valem para todos.
 * @param {Set<string>} selectedIds @param {string} recordId
 * @returns {{ ids: Set<string>, keepsSelection: boolean }}
 */
export function selectionForContextMenu(selectedIds, recordId) {
  if (selectedIds.has(recordId)) return { ids: selectedIds, keepsSelection: true };
  return { ids: new Set([recordId]), keepsSelection: false };
}
